import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Send, Paperclip, Clock, AlertTriangle, CheckCircle2, Download, User,
} from "lucide-react";
import api, { formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatDateTime } from "../lib/format";
import { Skeleton } from "../components/UI";

const PRIORITY_CLS = {
  Tinggi: "bg-red-50 text-red-700 border-red-200",
  Sedang: "bg-amber-50 text-amber-700 border-amber-200",
  Rendah: "bg-slate-50 text-slate-700 border-slate-200",
};
const STATUS_CLS = {
  "Open": "bg-blue-50 text-blue-700 border-blue-200",
  "On Process": "bg-amber-50 text-amber-700 border-amber-200",
  "Closed": "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const SLA_CLS = {
  "On Track": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "bg-emerald-500" },
  "Near Breach": { cls: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-500" },
  "Breached": { cls: "bg-red-50 text-red-700 border-red-200", bar: "bg-red-500" },
  "Resolved": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", bar: "bg-emerald-500" },
};
const ROLE_CLS = {
  KPPG: "bg-blue-50 text-blue-700",
  SPPG: "bg-amber-50 text-amber-700",
  Supplier: "bg-emerald-50 text-emerald-700",
  TPID: "bg-violet-50 text-violet-700",
};

const formatAge = (h) => {
  if (h == null) return "-";
  if (h < 0) {
    h = Math.abs(h);
    if (h < 1) return `${Math.round(h * 60)} mnt telat`;
    if (h < 48) return `${h.toFixed(1)} jam telat`;
    return `${(h / 24).toFixed(1)} hari telat`;
  }
  if (h < 1) return `${Math.round(h * 60)} menit`;
  if (h < 48) return `${h.toFixed(1)} jam`;
  return `${(h / 24).toFixed(1)} hari`;
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data.ticket);
      setComments(data.comments);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setPosting(true);
    setError("");
    try {
      await api.post(`/tickets/${id}/comments`, { content: reply });
      setReply("");
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPosting(false); }
  };

  const updateStatus = async (status) => {
    if (!window.confirm(`Ubah status ke "${status}"?`)) return;
    try {
      await api.patch(`/tickets/${id}/status`, { status });
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadAttachment = (i, name) => {
    const token = localStorage.getItem("kedan_token");
    fetch(`${API_BASE}/tickets/${id}/attachments/${i}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    });
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-48" /></div>;
  if (error) return <div className="kedan-card p-6 text-red-700">{error}</div>;
  if (!ticket) return null;

  const canChangeStatus = user.role === "KPPG" || user.id === ticket.assignee_id;
  const slaCfg = SLA_CLS[ticket.sla_status] || SLA_CLS["On Track"];
  const isClosed = ticket.status === "Closed";

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Kembali
      </button>

      {/* Header */}
      <div className="kedan-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500" data-testid="ticket-number">{ticket.ticket_number}</span>
              <span className={`kedan-badge ${PRIORITY_CLS[ticket.priority]}`} data-testid="ticket-priority">{ticket.priority}</span>
              <span className={`kedan-badge ${STATUS_CLS[ticket.status]}`} data-testid="ticket-status">{ticket.status}</span>
            </div>
            <h1 className="text-2xl font-display font-bold text-slate-900 mt-2" data-testid="ticket-subject">{ticket.subject}</h1>
            <div className="mt-2 text-xs text-slate-500">
              Dibuat oleh {ticket.creator_name} ({ticket.creator_role}) · {formatDateTime(ticket.created_at)}
            </div>
          </div>

          {/* SLA Card */}
          <div className={`px-4 py-3 rounded-md border ${slaCfg.cls} min-w-[200px]`} data-testid="sla-card">
            <div className="kedan-label opacity-80">SLA Status</div>
            <div className="text-lg font-display font-bold mt-0.5">{ticket.sla_status}</div>
            <div className="text-xs mt-0.5">
              Age: <span className="font-mono">{formatAge(ticket.age_hours)}</span> / {ticket.sla_hours}h
            </div>
            <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full ${slaCfg.bar} transition-all`} style={{ width: `${Math.min(ticket.sla_pct, 100)}%` }} />
            </div>
            <div className="text-[11px] mt-1 opacity-80">
              {isClosed ? "Tiket sudah selesai" : (
                ticket.sla_remaining_hours > 0
                  ? `${formatAge(ticket.sla_remaining_hours)} tersisa`
                  : `${formatAge(ticket.sla_remaining_hours)}`
              )}
            </div>
          </div>
        </div>

        {/* From / To */}
        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PartyCard label="DARI (Creator)" name={ticket.creator_name} role={ticket.creator_role} />
          <PartyCard label="KEPADA (Assignee)" name={ticket.assignee_name} role={ticket.assignee_role} />
        </div>

        {/* Status actions */}
        {canChangeStatus && !isClosed && (
          <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <span className="kedan-label text-slate-500">Ubah Status:</span>
            {ticket.status === "Open" && (
              <button onClick={() => updateStatus("On Process")} className="kedan-btn-secondary" data-testid="action-process">
                <Clock className="w-4 h-4" /> Mulai Proses
              </button>
            )}
            {ticket.status === "On Process" && (
              <button onClick={() => updateStatus("Open")} className="kedan-btn-secondary" data-testid="action-reopen-open">
                Kembali ke Open
              </button>
            )}
            <button onClick={() => updateStatus("Closed")} className="kedan-btn-primary" data-testid="action-close">
              <CheckCircle2 className="w-4 h-4" /> Tutup Tiket
            </button>
          </div>
        )}
        {canChangeStatus && isClosed && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <button onClick={() => updateStatus("On Process")} className="kedan-btn-secondary" data-testid="action-reopen">
              Buka Kembali (On Process)
            </button>
          </div>
        )}
      </div>

      {/* Description + attachments */}
      <div className="kedan-card p-6">
        <div className="kedan-label text-slate-500 mb-2">Deskripsi Kendala</div>
        <div className="text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">
          {ticket.description}
        </div>

        {ticket.attachments && ticket.attachments.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="kedan-label text-slate-500 mb-2">Lampiran ({ticket.attachments.length})</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="attachment-list">
              {ticket.attachments.map((a, i) => (
                <button key={i} onClick={() => downloadAttachment(i, a.name)}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200 hover:bg-slate-100 text-left"
                  data-testid={`attachment-${i}`}>
                  <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
                  <div className="flex-1 truncate">
                    <div className="text-sm truncate">{a.name}</div>
                    <div className="text-xs text-slate-500">{(a.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <Download className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Comments thread */}
      <div className="kedan-card p-6">
        <div className="kedan-label text-slate-500 mb-3">Diskusi ({comments.length})</div>
        <div className="space-y-4" data-testid="comment-list">
          {comments.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6 border border-dashed rounded-md">
              Belum ada balasan. Mulai diskusi di bawah.
            </div>
          )}
          {comments.map((c) => {
            const isSystem = c.content?.startsWith("_Status diubah");
            return (
              <div key={c.id} className={`flex gap-3 ${isSystem ? "opacity-75" : ""}`} data-testid={`comment-${c.id}`}>
                <div className={`w-9 h-9 rounded-full ${isSystem ? "bg-slate-100 text-slate-500" : "bg-[#045498] text-white"} flex items-center justify-center font-display font-semibold text-sm shrink-0`}>
                  {(c.author_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 text-sm">{c.author_name}</span>
                    <span className={`kedan-badge text-[10px] ${ROLE_CLS[c.author_role]}`}>{c.author_role}</span>
                    <span className="text-xs text-slate-500 ml-auto">{formatDateTime(c.created_at)}</span>
                  </div>
                  <div className={`mt-1.5 text-sm ${isSystem ? "italic text-slate-500" : "text-slate-700"} whitespace-pre-wrap`}>
                    {isSystem ? c.content.replace(/_/g, "").replace(/\*\*/g, "") : c.content}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!isClosed && (
          <form onSubmit={submitReply} className="mt-5 pt-5 border-t border-slate-100">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tulis Balasan</label>
            <textarea rows={3} className="kedan-input" value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder="Ketik balasan Anda..." data-testid="reply-input" />
            <div className="mt-2 flex justify-end">
              <button type="submit" disabled={!reply.trim() || posting} className="kedan-btn-primary" data-testid="reply-submit">
                <Send className="w-4 h-4" /> {posting ? "Mengirim..." : "Kirim Balasan"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PartyCard({ label, name, role }) {
  return (
    <div>
      <div className="kedan-label text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-display font-semibold text-sm">
          {name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
        </div>
        <div>
          <div className="font-medium text-slate-900 text-sm">{name}</div>
          <span className={`kedan-badge text-[10px] ${ROLE_CLS[role]}`}>{role}</span>
        </div>
      </div>
    </div>
  );
}
